import DateTimeSelector from './DateTimeSelector';
import { reservationEnums } from '@data/reservationStatusEnums';
import { Reservation } from '../resController';
import { translate } from '@data/translations';
import {
    ReservationSource,
    ReservationStatus,
    GuestTaxConfig,
    GuestTaxConfigAdult,
    GuestTaxConfigChild,
    TaxModel,
    TravelPurposeCode,
} from '@common/modelDefinition';
import { getCheckInAndCheckoutTimestamps } from '@pages/newReservations';
import useStore from '@data/state/zustand';
import { toast } from 'react-toastify';
import { BoardEnum } from '@pages/posTerminal/sideMenu/boardConsumption';
import * as React from 'react';

// IMPORT IZ UTILS - AŽURIRANO
import {
    parseGuestTaxConfig,
    getSuggestedItemByAge,
    DefaultPosItem,
    fillMissingGuestTaxConfig,
    getTaxAmountInfoLocal,
} from '../utils';

interface ReservationDateTimeSelectorProps {
    reservation: Reservation;
    onReservationUpdate: (res: Reservation) => void;
    isReadOnly?: boolean;
    isCondoInterface?: boolean;
}

const getISODate = (ts: number) => {
    return new Date(ts).toISOString().substr(0, 10);
};

// ============================================================================
// HELPER: Sinkroniziraj guestTaxConfig s novim brojevima
// ============================================================================
const syncGuestTaxConfig = (
    currentConfig: GuestTaxConfig | null,
    newAdultsCount: number,
    newChildrenCount: number,
    taxes: TaxModel[],
    defaultPosItems: DefaultPosItem[],
    reservationCheckIn?: number, // NOVO
    reservationCheckOut?: number, // NOVO
): GuestTaxConfig => {
    if (!currentConfig) {
        const adults: GuestTaxConfigAdult[] = [];
        const children: GuestTaxConfigChild[] = [];

        for (let i = 0; i < newAdultsCount; i++) {
            const suggestedTax = getSuggestedItemByAge(30, 'tax', taxes, defaultPosItems, 'adult');
            const suggestedInsurance = getSuggestedItemByAge(30, 'insurance', taxes, defaultPosItems, 'adult');
            adults.push({
                visaInfoId: null,
                fixedTaxId: suggestedTax.id,
                fixedTaxId2: suggestedInsurance.id,
                fixedTaxType: suggestedTax.sourceType,
                checkInTimestamp: reservationCheckIn || null, // NOVO
                checkOutTimestamp: reservationCheckOut || null, // NOVO
            });
        }

        for (let i = 0; i < newChildrenCount; i++) {
            const suggestedTax = getSuggestedItemByAge(5, 'tax', taxes, defaultPosItems, 'child');
            const suggestedInsurance = getSuggestedItemByAge(5, 'insurance', taxes, defaultPosItems, 'child');
            children.push({
                age: -1,
                visaInfoId: null,
                fixedTaxId: suggestedTax.id,
                fixedTaxId2: suggestedInsurance.id,
                fixedTaxType: suggestedTax.sourceType,
                checkInTimestamp: reservationCheckIn || null, // NOVO
                checkOutTimestamp: reservationCheckOut || null, // NOVO
            });
        }

        return { adults, children };
    }

    const newConfig: GuestTaxConfig = {
        adults: currentConfig.adults.map((a) => ({ ...a })),
        children: currentConfig.children.map((c) => ({ ...c })),
    };

    // === ADULTS ===
    const currentAdultsCount = newConfig.adults.length;

    if (newAdultsCount > currentAdultsCount) {
        // Dodaj nove placeholder goste
        for (let i = currentAdultsCount; i < newAdultsCount; i++) {
            const suggestedTax = getSuggestedItemByAge(30, 'tax', taxes, defaultPosItems, 'adult');
            const suggestedInsurance = getSuggestedItemByAge(30, 'insurance', taxes, defaultPosItems, 'adult');
            newConfig.adults.push({
                visaInfoId: null,
                fixedTaxId: suggestedTax.id,
                fixedTaxId2: suggestedInsurance.id,
                fixedTaxType: suggestedTax.sourceType,
                checkInTimestamp: reservationCheckIn || null, // NOVO
                checkOutTimestamp: reservationCheckOut || null, // NOVO
            });
        }
    } else if (newAdultsCount < currentAdultsCount) {
        // FIX: Sortiraj tako da gosti s visaInfoId budu PRVI (prijavljeni gosti ostaju)
        newConfig.adults.sort((a, b) => {
            if (a.visaInfoId && !b.visaInfoId) return -1;
            if (!a.visaInfoId && b.visaInfoId) return 1;
            return 0;
        });

        // Reži array na točan broj - UVIJEK (minimum 1)
        newConfig.adults = newConfig.adults.slice(0, Math.max(1, newAdultsCount));
    }

    // === CHILDREN ===
    const currentChildrenCount = newConfig.children.length;

    if (newChildrenCount > currentChildrenCount) {
        // Dodaj novu djecu
        for (let i = currentChildrenCount; i < newChildrenCount; i++) {
            const suggestedTax = getSuggestedItemByAge(5, 'tax', taxes, defaultPosItems, 'child');
            const suggestedInsurance = getSuggestedItemByAge(5, 'insurance', taxes, defaultPosItems, 'child');
            const taxInfo1 = getTaxAmountInfoLocal(suggestedTax.id, suggestedTax.sourceType, taxes, defaultPosItems);
            const taxInfo2 = getTaxAmountInfoLocal(
                suggestedInsurance.id,
                suggestedInsurance.sourceType,
                taxes,
                defaultPosItems,
            );
            newConfig.children.push({
                age: -1,
                visaInfoId: null,
                fixedTaxId: suggestedTax.id,
                fixedTaxId2: suggestedInsurance.id,
                fixedTaxType: suggestedTax.sourceType,
                checkInTimestamp: reservationCheckIn || null, // NOVO
                checkOutTimestamp: reservationCheckOut || null, // NOVO
                fixedTaxAmount1: taxInfo1.amount, // NOVO
                fixedTaxAmount2: taxInfo2.amount, // NOVO
                fixedTaxName1: taxInfo1.name, // NOVO
                fixedTaxName2: taxInfo2.name, // NOVO
            });
        }
    } else if (newChildrenCount < currentChildrenCount) {
        // FIX: Sortiraj tako da djeca s visaInfoId budu PRVA (prijavljena djeca ostaju)
        newConfig.children.sort((a, b) => {
            if (a.visaInfoId && !b.visaInfoId) return -1;
            if (!a.visaInfoId && b.visaInfoId) return 1;
            return 0;
        });

        // Reži array na točan broj
        newConfig.children = newConfig.children.slice(0, newChildrenCount);
    }

    return newConfig;
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
function ReservationDateTimeSelector(props: ReservationDateTimeSelectorProps) {
    const { reservation, onReservationUpdate, isReadOnly = false, isCondoInterface = false } = props;
    const { genuineInvoiceIssued } = reservation;
    const disableCheckIn = reservation.statusEnum === reservationEnums.checkedIn;
    const disableChangingDateIfProviderReservation = reservation.providerReservation ? true : false;

    const otherSettings = useStore((appState: any) => appState.otherSettings);
    const taxes: TaxModel[] = useStore((appState: any) => appState.model.Tax) || [];
    // DODANO: Dohvati defaultPosItems
    const invoicingSettings = useStore((appState: any) => appState.invoicingSettings);
    const defaultPosItems: DefaultPosItem[] = invoicingSettings?.defaultPosItems || [];
    const hasAutoFixed = React.useRef<number | null>(null);

    // State za dropdown
    const [showChildrenDropdown, setShowChildrenDropdown] = React.useState(false);

    // Temp state za editing (dok ne klikne Potvrdi)
    const [tempChildrenAges, setTempChildrenAges] = React.useState<number[]>([]);

    // Parse guestTaxConfig
    const guestTaxConfig = parseGuestTaxConfig(reservation);
    const childrenCount = reservation.childrenNumber || 0;

    // Izvuci dobi djece
    const childrenAges: number[] = React.useMemo(() => {
        if (!guestTaxConfig?.children) return [];
        return guestTaxConfig.children.map((c) => c.age);
    }, [guestTaxConfig]);

    // Provjeri da li neka djeca nemaju dob
    // const hasChildrenWithoutAge =
    //     childrenAges.some((age) => age < 0) || (childrenCount > 0 && childrenAges.length < childrenCount);

    // Sync temp ages when dropdown opens
    React.useEffect(() => {
        if (showChildrenDropdown) {
            const ages: number[] = [];
            for (let i = 0; i < childrenCount; i++) {
                ages.push(childrenAges[i] !== undefined ? childrenAges[i] : -1);
            }
            setTempChildrenAges(ages);
        }
    }, [showChildrenDropdown, childrenCount, childrenAges]);

    // Auto-otvori dropdown SAMO kada se poveća broj djece UNUTAR ISTE rezervacije
    // i samo ako nova djeca nemaju unesenu dob
    const prevChildrenCount = React.useRef(childrenCount);
    const prevReservationId = React.useRef(reservation.id);

    React.useEffect(() => {
        // Ako se promijenila rezervacija (npr. promjena taba), samo resetiraj ref
        if (reservation.id !== prevReservationId.current) {
            prevChildrenCount.current = childrenCount;
            prevReservationId.current = reservation.id;
            return; // NE otvaraj dropdown
        }

        // Otvori dropdown samo ako se POVEĆAO broj djece I nova djeca nemaju dob
        if (childrenCount > prevChildrenCount.current && childrenCount > 0) {
            const newChildrenWithoutAge =
                childrenAges.length < childrenCount ||
                childrenAges.slice(prevChildrenCount.current).some((age) => age < 0);

            if (newChildrenWithoutAge) {
                setShowChildrenDropdown(true);
            }
        }

        prevChildrenCount.current = childrenCount;
    }, [childrenCount, reservation.id, childrenAges]);

    React.useEffect(() => {
        if (
            !reservation.id ||
            hasAutoFixed.current === reservation.id ||
            reservation.statusEnum === ReservationStatus.closed
        )
            return;

        const fixedConfig = fillMissingGuestTaxConfig(reservation, taxes, defaultPosItems);

        if (fixedConfig) {
            hasAutoFixed.current = reservation.id;
            const updatedRes = { ...reservation };
            updatedRes.guestTaxConfig = fixedConfig;
            onReservationUpdate(updatedRes);
        } else {
            hasAutoFixed.current = reservation.id;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reservation.id]);

    // ========================================================================
    // HANDLER - promjena broja gostiju
    // AŽURIRANO: adultsNumber je sada direktno broj odraslih, ne total guests
    // ========================================================================
    const reservationsNumberInputHandler = (e: any) => {
        try {
            const res = { ...reservation };
            const fieldName = e.target.name;
            const newValue = Number(e.target.value);

            (res as any)[fieldName] = newValue;

            // AŽURIRANO: Direktno koristi vrijednosti bez oduzimanja
            const newAdultsCount = fieldName === 'adultsNumber' ? newValue : res.adultsNumber || 1;
            const newChildrenCount = fieldName === 'childrenNumber' ? newValue : res.childrenNumber || 0;

            const currentConfig = parseGuestTaxConfig(reservation);

            const newConfig = syncGuestTaxConfig(
                currentConfig,
                newAdultsCount,
                newChildrenCount,
                taxes,
                defaultPosItems,
                reservation.checkInTimestamp, // NOVO
                reservation.checkOutTimestamp, // NOVO
            );
            res.guestTaxConfig = newConfig;

            onReservationUpdate(res);
        } catch (error: any) {
            toast(translate(error.message), { type: toast.TYPE.ERROR });
        }
    };

    // ========================================================================
    // HANDLER - potvrdi promjene dobi
    // ========================================================================
    const handleConfirmAges = () => {
        const res = { ...reservation };

        let newConfig: GuestTaxConfig;
        if (!guestTaxConfig) {
            // AŽURIRANO: Direktno koristi adultsNumber bez oduzimanja
            const newAdultsCount = reservation.adultsNumber || 1;
            newConfig = syncGuestTaxConfig(null, newAdultsCount, childrenCount, taxes, defaultPosItems);
        } else {
            newConfig = {
                adults: [...guestTaxConfig.adults],
                children: [...guestTaxConfig.children],
            };
        }

        // Ažuriraj sve dobi
        for (let i = 0; i < tempChildrenAges.length; i++) {
            const newAge = tempChildrenAges[i];

            while (newConfig.children.length <= i) {
                const suggestedTax = getSuggestedItemByAge(5, 'tax', taxes, defaultPosItems, 'child');
                const suggestedInsurance = getSuggestedItemByAge(5, 'insurance', taxes, defaultPosItems, 'child');
                const taxInfo1 = getTaxAmountInfoLocal(
                    suggestedTax.id,
                    suggestedTax.sourceType,
                    taxes,
                    defaultPosItems,
                );
                const taxInfo2 = getTaxAmountInfoLocal(
                    suggestedInsurance.id,
                    suggestedInsurance.sourceType,
                    taxes,
                    defaultPosItems,
                );
                newConfig.children.push({
                    age: -1,
                    visaInfoId: null,
                    fixedTaxId: suggestedTax.id,
                    fixedTaxId2: suggestedInsurance.id,
                    fixedTaxType: suggestedTax.sourceType,
                    fixedTaxAmount1: taxInfo1.amount, // NOVO
                    fixedTaxAmount2: taxInfo2.amount, // NOVO
                    fixedTaxName1: taxInfo1.name, // NOVO
                    fixedTaxName2: taxInfo2.name, // NOVO
                    checkInTimestamp: reservation.checkInTimestamp,
                    checkOutTimestamp: reservation.checkOutTimestamp,
                });
            }

            const suggestedTax = getSuggestedItemByAge(
                newAge >= 0 ? newAge : 5,
                'tax',
                taxes,
                defaultPosItems,
                'child',
            );
            const suggestedInsurance = getSuggestedItemByAge(
                newAge >= 0 ? newAge : 5,
                'insurance',
                taxes,
                defaultPosItems,
                'child',
            );
            newConfig.children[i] = {
                ...newConfig.children[i],
                age: newAge,
                fixedTaxId: suggestedTax.id,
                fixedTaxId2: suggestedInsurance.id,
                fixedTaxType: suggestedTax.sourceType,
            };
        }

        res.guestTaxConfig = newConfig;
        onReservationUpdate(res);
        setShowChildrenDropdown(false);
    };

    // ========================================================================
    // HANDLER - promjena temp dobi u dropdownu
    // ========================================================================
    const handleTempAgeChange = (index: number, newAge: number) => {
        const newAges = [...tempChildrenAges];
        newAges[index] = newAge;
        setTempChildrenAges(newAges);
    };

    const minMaxConstraintsFix = (e: any) => {
        try {
            if (e.target.max < Number(e.target.value) || e.target.min > Number(e.target.value)) {
                const res = { ...reservation };
                if (e.target.max < Number(e.target.value)) {
                    (res as any)[e.target.name] = Number(e.target.max);
                    onReservationUpdate(res);
                } else {
                    (res as any)[e.target.name] = Number(e.target.min);
                    onReservationUpdate(res);
                }
            }
        } catch (error: any) {
            toast(translate(error.message), { type: toast.TYPE.ERROR });
        }
    };

    const dateTimePickHandler = (type: string, timestamp: number) => {
        const res = { ...reservation };

        // NOVO: Spremi STARE datume prije promjene
        const oldCheckIn = reservation.checkInTimestamp;
        const oldCheckOut = reservation.checkOutTimestamp;

        (res as any)[type] = timestamp;

        if (type === 'checkOutTimestamp') {
            if (getISODate(res.checkInTimestamp) === getISODate(res.checkOutTimestamp)) {
                if (res.checkInTimestamp >= res.checkOutTimestamp) {
                    res.checkOutTimestamp = res.checkInTimestamp + 1 * 60 * 60 * 1000;
                }
            } else if (res.checkInTimestamp >= res.checkOutTimestamp) {
                res.checkOutTimestamp = res.checkInTimestamp + 24 * 60 * 60 * 1000;
                const { checkOutTimestamp } = getCheckInAndCheckoutTimestamps(
                    otherSettings,
                    new Date(res.checkInTimestamp),
                    new Date(res.checkOutTimestamp),
                );
                res.checkOutTimestamp = checkOutTimestamp;
            }
        } else {
            if (res.checkInTimestamp >= res.checkOutTimestamp) {
                res.checkOutTimestamp = res.checkInTimestamp + 24 * 60 * 60 * 1000;
                const { checkOutTimestamp } = getCheckInAndCheckoutTimestamps(
                    otherSettings,
                    new Date(res.checkInTimestamp),
                    new Date(res.checkOutTimestamp),
                );
                res.checkOutTimestamp = checkOutTimestamp;
            }
        }

        // NOVO: Sinkroniziraj datume gostiju koji PRATE reservation datume
        // Gost PRATI ako njegovi datumi = STARI reservation datumi
        // Gost ima CUSTOM datume ako su različiti od reservation
        const parsedConfig: GuestTaxConfig | null = parseGuestTaxConfig(res);
        if (parsedConfig) {
            const newConfig: GuestTaxConfig = {
                adults: parsedConfig.adults.map((a: GuestTaxConfigAdult) => {
                    // Provjeri da li gost PRATI reservation datume
                    const followsResDates = a.checkInTimestamp === oldCheckIn && a.checkOutTimestamp === oldCheckOut;

                    // Ili nema datume uopće - defaulta na res datume
                    const hasNoDates = !a.checkInTimestamp || !a.checkOutTimestamp;

                    if (followsResDates || hasNoDates) {
                        return {
                            ...a,
                            checkInTimestamp: res.checkInTimestamp,
                            checkOutTimestamp: res.checkOutTimestamp,
                        };
                    }
                    // CUSTOM datumi - ne diraj
                    return a;
                }),
                children: parsedConfig.children.map((c: GuestTaxConfigChild) => {
                    const followsResDates = c.checkInTimestamp === oldCheckIn && c.checkOutTimestamp === oldCheckOut;

                    const hasNoDates = !c.checkInTimestamp || !c.checkOutTimestamp;

                    if (followsResDates || hasNoDates) {
                        return {
                            ...c,
                            checkInTimestamp: res.checkInTimestamp,
                            checkOutTimestamp: res.checkOutTimestamp,
                        };
                    }
                    return c;
                }),
            };
            res.guestTaxConfig = newConfig;
        }

        try {
            onReservationUpdate(res);
        } catch (error: any) {
            toast(translate(error.message), { type: toast.TYPE.ERROR });
        }
    };

    // Check if temp ages have any missing
    const tempHasMissingAges = tempChildrenAges.some((age) => age < 0);

    return (
        <div className="">
            <div className="display-flex mobile-page-form">
                <div className="flex mr-16p">
                    <label className="text-bold ml-4p mb-4p">{translate('Check in')}:</label>
                    <DateTimeSelector
                        readOnly={
                            disableChangingDateIfProviderReservation ||
                            disableCheckIn ||
                            isReadOnly ||
                            genuineInvoiceIssued === 1 ||
                            reservation.statusEnum === ReservationStatus.closed
                        }
                        disabledBefore={new Date()}
                        dateTimePickHandler={(timestamp: number) => {
                            dateTimePickHandler('checkInTimestamp', timestamp);
                        }}
                        value={reservation.checkInTimestamp}
                    />
                </div>
                <div className="flex ml-16p mobile-ml-0">
                    <label className="text-bold ml-4p mb-4p">{translate('Check out')}:</label>
                    <DateTimeSelector
                        readOnly={
                            disableChangingDateIfProviderReservation ||
                            isReadOnly ||
                            genuineInvoiceIssued === 1 ||
                            reservation.statusEnum === ReservationStatus.closed
                        }
                        disabledBefore={new Date(reservation.checkInTimestamp)}
                        dateTimePickHandler={(timestamp: number) => {
                            dateTimePickHandler('checkOutTimestamp', timestamp);
                        }}
                        value={reservation.checkOutTimestamp}
                    />
                </div>
            </div>

            <div className="display-flex mt-12p mobile-page-form justify-content-between">
                <div className="flex mr-8p">
                    <div className="display-flex">
                        <div className="mr-16p">
                            {/* AŽURIRANO: "Total guests" -> "Adults" */}
                            <label className="text-bold ml-4p mb-4p">{translate('Adults')}:</label>
                            <br />
                            <input
                                disabled={isReadOnly || genuineInvoiceIssued === 1}
                                name="adultsNumber"
                                min={1}
                                max={20}
                                onChange={reservationsNumberInputHandler}
                                value={reservation.adultsNumber ? reservation.adultsNumber : 1}
                                onBlur={minMaxConstraintsFix}
                                type="number"
                                className="input input-modal"
                                style={{ width: '100px' }}
                            />
                        </div>
                        <div className="ml-16p">
                            <label className="text-bold ml-4p mb-4p">{translate('Children')}:</label>
                            <br />
                            <div style={{ position: 'relative', display: 'inline-block', width: '100px' }}>
                                <input
                                    disabled={isReadOnly || genuineInvoiceIssued === 1}
                                    name="childrenNumber"
                                    min={0}
                                    max={20}
                                    onChange={reservationsNumberInputHandler}
                                    value={reservation.childrenNumber ? reservation.childrenNumber : 0}
                                    onBlur={minMaxConstraintsFix}
                                    type="number"
                                    className="input input-modal"
                                    style={{
                                        width: '100px',
                                        paddingRight: '28px', // UVIJEK padding za strelicu
                                    }}
                                />
                                {/* Strelica UVIJEK prisutna */}
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        if (childrenCount > 0) {
                                            setShowChildrenDropdown(!showChildrenDropdown);
                                        }
                                    }}
                                    disabled={childrenCount === 0}
                                    style={{
                                        position: 'absolute',
                                        right: '4px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        padding: '4px 6px',
                                        backgroundColor: 'transparent',
                                        border: 'none',
                                        cursor: childrenCount > 0 ? 'pointer' : 'default',
                                        fontSize: '12px',
                                        color: childrenCount > 0 ? '#6c757d' : '#dee2e6',
                                        opacity: childrenCount > 0 ? 1 : 0.5,
                                    }}
                                    title={childrenCount > 0 ? translate('Set children ages') : ''}
                                >
                                    <i
                                        className={`fa fa-chevron-${showChildrenDropdown && childrenCount > 0 ? 'up' : 'down'}`}
                                    />
                                </button>

                                {/* DROPDOWN */}
                                {childrenCount > 0 && showChildrenDropdown && (
                                    <div
                                        style={{
                                            position: 'absolute',
                                            top: '100%',
                                            left: 0,
                                            marginTop: '4px',
                                            backgroundColor: '#f8f9fa',
                                            border: '1px solid #dee2e6',
                                            borderRadius: '8px',
                                            padding: '12px',
                                            zIndex: 1000,
                                            minWidth: '200px',
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                        }}
                                    >
                                        {/* Info ako treba unos */}
                                        {tempHasMissingAges && (
                                            <div
                                                style={{
                                                    fontSize: '12px',
                                                    color: '#856404',
                                                    marginBottom: '10px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <i className="fa fa-info-circle mr-4p" style={{ color: '#ffc107' }} />
                                                {translate('Set children ages for correct pricing')}
                                            </div>
                                        )}

                                        {/* Select-ovi za dob */}
                                        <div className="display-flex flex-wrap" style={{ gap: '8px' }}>
                                            {tempChildrenAges.map((age, index) => (
                                                <select
                                                    key={index}
                                                    value={age}
                                                    onChange={(e) => handleTempAgeChange(index, Number(e.target.value))}
                                                    disabled={isReadOnly || genuineInvoiceIssued === 1}
                                                    className="input input-modal"
                                                    style={{
                                                        width: '70px',
                                                        padding: '6px 8px',
                                                        fontSize: '13px',
                                                        borderColor: age < 0 ? '#ffc107' : '#28a745',
                                                        backgroundColor: 'white',
                                                    }}
                                                >
                                                    <option value={-1}>?</option>
                                                    {Array.from({ length: 18 }, (_, i) => (
                                                        <option key={i} value={i}>
                                                            {i} {translate('yrs')}
                                                        </option>
                                                    ))}
                                                </select>
                                            ))}
                                        </div>

                                        {/* Potvrdi button */}
                                        <div style={{ marginTop: '12px', textAlign: 'right' }}>
                                            <button
                                                type="button"
                                                className="button-primary"
                                                onClick={handleConfirmAges}
                                                style={{
                                                    padding: '6px 16px',
                                                    fontSize: '13px',
                                                }}
                                            >
                                                <i className="fa fa-check mr-4p" />
                                                {translate('Confirm')}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                <div
                    className={
                        !isCondoInterface
                            ? 'display-flex flex-center-center mobile-mt-12p'
                            : 'flex flex-column flex-center-center mobile-mt-12p'
                    }
                    style={{ marginLeft: !isCondoInterface ? '44px' : '' }}
                >
                    <div className="">
                        <div className="ml-16p">
                            <label className="text-bold mr-4p mb-4p" htmlFor={''}>
                                {translate('Board')}:
                            </label>
                            <div className="">
                                <select
                                    onChange={async (e: any) => {
                                        const res = { ...reservation };
                                        res.board = e.target.value;
                                        onReservationUpdate(res);
                                    }}
                                    name="board"
                                    value={reservation.board}
                                    className="input input-modal"
                                    style={{ width: '120px' }}
                                >
                                    {Object.keys(BoardEnum).map((board: string, key: number) => {
                                        return (
                                            <option value={board} key={key}>
                                                {translate(BoardEnum[board as keyof typeof BoardEnum])}
                                            </option>
                                        );
                                    })}
                                </select>
                            </div>
                        </div>
                    </div>
                    {!isCondoInterface ? (
                        <div className="ml-16p">
                            <label className="text-bold mb-4p" htmlFor={''}>
                                {translate('SourceRes')}:
                            </label>
                            <select
                                onChange={async (e: any) => {
                                    if (
                                        !(
                                            reservation?.providerReservation &&
                                            reservation.reservationSource === ReservationSource.online_booking
                                        )
                                    ) {
                                        const res = { ...reservation };
                                        res.reservationSource = e.target.value === '' ? null : parseInt(e.target.value);
                                        onReservationUpdate(res);
                                    }
                                }}
                                name="reservationSource"
                                value={reservation.reservationSource || ''}
                                className="input input-modal"
                                style={{
                                    width: '126px',
                                    backgroundColor:
                                        reservation?.providerReservation &&
                                        reservation.reservationSource === ReservationSource.online_booking
                                            ? '#f8f9fa'
                                            : 'white',
                                    color:
                                        reservation?.providerReservation &&
                                        reservation.reservationSource === ReservationSource.online_booking
                                            ? '#212529'
                                            : 'inherit',
                                    opacity:
                                        reservation?.providerReservation &&
                                        reservation.reservationSource === ReservationSource.online_booking
                                            ? 1
                                            : 1,
                                    cursor:
                                        reservation?.providerReservation &&
                                        reservation.reservationSource === ReservationSource.online_booking
                                            ? 'not-allowed'
                                            : 'pointer',
                                }}
                                disabled={
                                    reservation?.providerReservation &&
                                    reservation.reservationSource === ReservationSource.online_booking
                                }
                            >
                                <option value="">{translate('Select source')}</option>
                                {Object.keys(ReservationSource)
                                    .filter((key) => isNaN(Number(key)))
                                    .map((key: string, index: number) => {
                                        const value = ReservationSource[key as keyof typeof ReservationSource];
                                        return (
                                            <option value={value} key={index}>
                                                {translate(key)}
                                            </option>
                                        );
                                    })}
                            </select>
                        </div>
                    ) : null}
                </div>
            </div>
            <div className='flex-center-between mt-12p'>
                <div className="display-flex">
                    <label className="text-bold ml-4p mb-4p mr-8p">{translate('Room beds (+ spare beds)')}:</label>
                    <div className="">
                        {reservation.RoomInfo?.bedCount}
                        {' + '}
                        {reservation?.RoomInfo?.spareBedCount}
                    </div>
                </div>
                <div className="ml-16p">
                    <label className="text-bold ml-4p mb-4p">{translate('TravelPurpose')}:</label>
                    <br />
                    <select
                        onChange={async (e: any) => {
                            const res = { ...reservation };
                            res.travelPurposeCode = e.target.value === '' ? null : parseInt(e.target.value);
                            onReservationUpdate(res);
                        }}
                        name="travelPurposeCode"
                        value={reservation.travelPurposeCode || ''}
                        className="input input-modal"
                        disabled={isReadOnly || genuineInvoiceIssued === 1}
                        style={{
                            width: '126px',
                            cursor: isReadOnly || genuineInvoiceIssued === 1 ? 'not-allowed' : 'pointer',
                        }}
                    >
                        <option value="">{translate('Select purpose')}</option>
                        {Object.keys(TravelPurposeCode)
                            .filter((key) => isNaN(Number(key)))
                            .map((key: string, index: number) => {
                                const value = TravelPurposeCode[key as keyof typeof TravelPurposeCode];
                                return (
                                    <option value={value} key={index}>
                                        {translate(key)}
                                    </option>
                                );
                            })}
                    </select>
                </div>
            </div>
        </div>
    );
}

export default ReservationDateTimeSelector;